"use client";

import { type ChangeEvent, useCallback, useEffect, useRef, useState } from "react";

import { mimeToExpertUploadExtension, normalizeExpertAudioMime } from "@/lib/expert-audio-mime";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import { createSupabaseBrowserClient } from "@/lib/supabase/client";

const MAX_MS = 600_000;
/** Matches Storage bucket `file_size_limit` (migration `20260505120000_expert_audio_bucket_upload_friendly.sql`). */
const MAX_UPLOAD_BYTES = 104_857_600;

function resolveUploadedAudioMime(file: File): string | null {
  if (file.type) {
    const n = normalizeExpertAudioMime(file.type);
    if (n) return n;
  }
  const ext = file.name.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "webm":
      return "audio/webm";
    case "m4a":
    case "mp4":
      return "audio/mp4";
    case "mp3":
      return "audio/mpeg";
    case "wav":
      return "audio/wav";
    default:
      return null;
  }
}

function probeAudioDurationSeconds(file: Blob): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const audio = document.createElement("audio");
    audio.preload = "metadata";
    const cleanup = () => URL.revokeObjectURL(url);
    audio.onloadedmetadata = () => {
      cleanup();
      const d = audio.duration;
      if (!Number.isFinite(d) || d <= 0) reject(new Error("duration"));
      else resolve(d);
    };
    audio.onerror = () => {
      cleanup();
      reject(new Error("load"));
    };
    audio.src = url;
  });
}

function pickMime(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  const prefer = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
  return prefer.find((m) => MediaRecorder.isTypeSupported(m));
}

type Phase = "idle" | "recording" | "preview";

type Props = {
  orderId: string;
  locked: boolean;
  onSubmitted?: () => void;
  /** `chat` = bottom composer bar (iMessage-style). */
  variant?: "card" | "chat";
};

export function ExpertVoiceRecorder({ orderId, locked, onSubmitted, variant = "card" }: Props) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState("");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const tickRef = useRef<number | null>(null);
  const startRef = useRef<number>(0);
  const durationMsRef = useRef(0);
  const mimeRef = useRef<string>("audio/webm");
  const blobRef = useRef<Blob | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const stopTracks = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      if (tickRef.current) window.clearInterval(tickRef.current);
      stopTracks();
    };
  }, [stopTracks]);

  const startRecording = async () => {
    setError("");
    chunksRef.current = [];
    blobRef.current = null;
    const mime = pickMime();
    if (!mime) {
      setError("Recording not supported in this browser. Try Chrome on desktop.");
      return;
    }
    mimeRef.current = mime;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream, { mimeType: mime });
      mediaRecorderRef.current = mr;
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        durationMsRef.current = Math.min(Date.now() - startRef.current, MAX_MS);
        setElapsedMs(durationMsRef.current);
        const blob = new Blob(chunksRef.current, { type: mimeRef.current });
        blobRef.current = blob;
        stopTracks();
        mediaRecorderRef.current = null;
        setPhase("preview");
      };
      mr.start(250);
      startRef.current = Date.now();
      setElapsedMs(0);
      setPhase("recording");
      tickRef.current = window.setInterval(() => {
        const ms = Date.now() - startRef.current;
        setElapsedMs(ms);
        if (ms >= MAX_MS) {
          void stopRecording();
        }
      }, 300);
    } catch {
      setError("Microphone permission denied or unavailable.");
    }
  };

  const stopRecording = async () => {
    if (tickRef.current) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    const mr = mediaRecorderRef.current;
    if (mr && mr.state === "recording") {
      mr.stop();
    } else {
      stopTracks();
      setPhase("idle");
    }
  };

  const startOver = () => {
    setError("");
    blobRef.current = null;
    chunksRef.current = [];
    durationMsRef.current = 0;
    setElapsedMs(0);
    setPhase("idle");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const uploadVoiceMemo = useCallback(
    async (blob: Blob, durationSeconds: number, mimeRaw: string) => {
      const mimeType = normalizeExpertAudioMime(mimeRaw) ?? "audio/webm";
      setSubmitting(true);
      setError("");
      try {
        const ext = mimeToExpertUploadExtension(mimeType);
        if (!ext) {
          setError("Unsupported audio type.");
          return;
        }
        const path = `${orderId}/${Date.now()}.${ext}`;
        const sb = createSupabaseBrowserClient();
        const { error: upErr } = await sb.storage.from("expert-audio").upload(path, blob, {
          contentType: mimeType,
          upsert: false,
        });
        if (upErr) {
          setError(upErr.message || "Upload failed.");
          return;
        }

        const finJson = await invokeEdgeFunction<{ error?: string }>("expert-audio-finalize", {
          orderId,
          storagePath: path,
          durationSeconds,
          mimeType,
        });
        if (finJson && typeof finJson === "object" && "error" in finJson && finJson.error) {
          setError(finJson.error);
          return;
        }

        onSubmitted?.();
      } catch {
        setError("Network error.");
      } finally {
        setSubmitting(false);
      }
    },
    [orderId, onSubmitted],
  );

  const submitRecording = async () => {
    const blob = blobRef.current;
    if (!blob || locked) return;
    const durationMs = Math.min(durationMsRef.current || 1, MAX_MS);
    const durationSeconds = Math.max(1, Math.round(durationMs / 1000));
    if (durationSeconds > MAX_MS / 1000) {
      setError("Recording exceeds 10 minutes.");
      return;
    }

    await uploadVoiceMemo(blob, durationSeconds, mimeRef.current || "audio/webm");
  };

  const onPickAudioFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || locked) return;

    setError("");
    if (file.size > MAX_UPLOAD_BYTES) {
      setError("File is too large (max 100 MB).");
      return;
    }

    const mimeType = resolveUploadedAudioMime(file);
    if (!mimeType) {
      setError("Use a supported audio file (webm, m4a, mp3, wav).");
      return;
    }

    let durationSeconds: number;
    try {
      const d = await probeAudioDurationSeconds(file);
      durationSeconds = Math.max(1, Math.round(d));
    } catch {
      setError("Could not read that audio file. Try another format.");
      return;
    }

    if (durationSeconds > MAX_MS / 1000) {
      setError("Audio must be 10 minutes or shorter.");
      return;
    }

    await uploadVoiceMemo(file, durationSeconds, mimeType);
  };

  const fmt = (ms: number) => {
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, "0")}`;
  };

  if (locked) {
    return null;
  }

  const hiddenFileInput = (
    <input
      ref={fileInputRef}
      type="file"
      className="hidden"
      accept="audio/webm,audio/mp4,audio/mpeg,audio/wav,.m4a,.mp3,.webm,.wav"
      onChange={(ev) => void onPickAudioFile(ev)}
    />
  );

  if (variant === "chat") {
    return (
      <div className="w-full">
        {hiddenFileInput}
        {error ? <p className="mb-2 px-1 text-center text-xs font-medium text-rose-600">{error}</p> : null}
        <div className="flex min-h-[52px] w-full items-center gap-2 rounded-[28px] border border-black/[0.08] bg-white/95 px-3 py-2 shadow-[0_1px_4px_rgba(0,0,0,0.06)] backdrop-blur-md">
          {phase === "idle" ? (
            <>
              <button
                type="button"
                aria-label="Record voice memo"
                onClick={() => void startRecording()}
                className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[#FF3B30] text-white shadow-md transition active:scale-95"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14C5.98 15.36 8.79 18 12 18s6.02-2.64 6.91-5.86c.1-.6-.39-1.14-1-1.14z" />
                </svg>
              </button>
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-semibold text-zinc-900">Voice memo</p>
                <p className="text-[12px] leading-tight text-zinc-500">Tap red · max 10:00 · or upload audio</p>
              </div>
              <button
                type="button"
                disabled={submitting}
                onClick={() => fileInputRef.current?.click()}
                className="shrink-0 rounded-full bg-zinc-200 px-3 py-2 text-[12px] font-bold text-zinc-800 disabled:opacity-50"
              >
                Upload
              </button>
            </>
          ) : null}

          {phase === "recording" ? (
            <>
              <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[#FF3B30]/15">
                <span className="relative flex size-3">
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-[#FF3B30] opacity-40" />
                  <span className="relative inline-flex size-3 rounded-full bg-[#FF3B30]" />
                </span>
              </div>
              <span className="flex-1 font-mono text-[17px] font-semibold tabular-nums text-zinc-900">{fmt(elapsedMs)}</span>
              <button
                type="button"
                onClick={() => void stopRecording()}
                className="shrink-0 rounded-full bg-zinc-900 px-4 py-2 text-[14px] font-bold text-white active:scale-[0.98]"
              >
                Stop
              </button>
            </>
          ) : null}

          {phase === "preview" ? (
            <>
              <span className="flex-1 text-[14px] font-semibold text-zinc-800">Ready · {fmt(elapsedMs)}</span>
              <button
                type="button"
                onClick={startOver}
                disabled={submitting}
                className="shrink-0 rounded-full bg-zinc-200 px-3 py-2 text-[13px] font-bold text-zinc-800 disabled:opacity-50"
              >
                Redo
              </button>
              <button
                type="button"
                onClick={() => void submitRecording()}
                disabled={submitting}
                className="shrink-0 rounded-full bg-[#007AFF] px-4 py-2 text-[14px] font-bold text-white shadow-sm disabled:opacity-50"
              >
                {submitting ? "…" : "Send"}
              </button>
            </>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-xl border border-violet-200 bg-violet-50/60 p-4">
      {hiddenFileInput}
      <p className="text-xs font-bold uppercase tracking-wide text-violet-800">Voice memo (max 10:00)</p>
      <p className="mt-2 text-xs text-zinc-600">
        Record here, or upload webm / m4a / mp3 / wav from your phone or computer (max 100 MB). You can start over before
        submitting.
      </p>

      {error ? <p className="mt-2 text-sm font-medium text-rose-600">{error}</p> : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {phase === "idle" ? (
          <>
            <button
              type="button"
              onClick={() => void startRecording()}
              className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-bold text-white shadow hover:bg-rose-700"
            >
              Start recording
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={() => fileInputRef.current?.click()}
              className="rounded-lg border border-violet-300 bg-white px-4 py-2 text-sm font-bold text-violet-900 shadow-sm hover:bg-violet-50 disabled:opacity-50"
            >
              Upload audio file
            </button>
          </>
        ) : null}

        {phase === "recording" ? (
          <>
            <span className="font-mono text-sm font-bold tabular-nums text-zinc-900">{fmt(elapsedMs)}</span>
            <button
              type="button"
              onClick={() => void stopRecording()}
              className="rounded-lg bg-zinc-900 px-4 py-2 text-sm font-bold text-white hover:bg-zinc-800"
            >
              Stop
            </button>
          </>
        ) : null}

        {phase === "preview" ? (
          <>
            <span className="text-sm font-medium text-zinc-700">Recorded {fmt(elapsedMs)}</span>
            <button
              type="button"
              onClick={startOver}
              disabled={submitting}
              className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm font-bold text-zinc-800 hover:bg-zinc-50 disabled:opacity-50"
            >
              Start over
            </button>
            <button
              type="button"
              onClick={() => void submitRecording()}
              disabled={submitting}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-bold text-white shadow hover:bg-violet-700 disabled:opacity-50"
            >
              {submitting ? "Submitting…" : "Submit voice memo"}
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}
